/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddCustomStringModalComponent } from './modals/add-custom-string.component';
import { SelectCountryModalComponent } from './modals/select-country.component';
import { StandardRedactionFieldsModalComponent } from './modals/standard-redaction-fields-modal.component';
import { UserService } from '../../services/user.service';
import { XrxControlBaseComponent } from '../controls/xrx-control-base.component';
import { SettingType } from '../../../common/enums';
import { OptionListOptions } from '../../services/workflow.service';

@Component({
  selector: 'xrx-redact',
  templateUrl: './xrx-redact.component.html',
  styleUrls: ['./xrx-redact.component.less'],
  providers: [
    { provide: XrxControlBaseComponent, useExisting: XrxRedactComponent },
  ],
  standalone: false,
})
export class XrxRedactComponent
  extends XrxControlBaseComponent
  implements OnInit
{
  @Input() required: boolean;
  @Input() label: string;
  // store the redact configuration
  @Input() value: string;
  // store the selected country key
  @Input() value2: string;
  @Input() isMfd: boolean;
  @Input() isAutomated = false;
  @ViewChild('customWordForm', { static: true }) public cwForm: NgForm;

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change = new EventEmitter<string>();
  @Output() change2 = new EventEmitter<string>();
  @Output() validation = new EventEmitter<boolean>();
  @Output() valueChange = new EventEmitter<string>();
  @Output() value2Change = new EventEmitter<string>();

  isInvalid = false;
  cwFormIsInvalid = false;
  defaultFieldIsInvalid = false;
  customFieldIsInvalid = false;
  baseUrl = 'api/redaction/';
  langCode = navigator.language.startsWith('pt') ? 'pt-br' : navigator.language;
  countryCode: string;
  countries: CountryOption[] = [];
  selectedCountry: CountryOption = { key: '', value: '' };
  redactConfig: RedactConfig = {
    markupType: '',
    standardFields: [],
    customFields: [],
  };
  standardFields: StandardField[];
  defaultFields: StandardField[] = [];
  customFields: CustomField[] = [];
  customFieldId = 0;
  isLoading = true;

  addCustomWordDisabled = false;
  standardFieldsLabel: string;
  isSavedConfig: boolean;

  matchOptions: OptionListOptions[] = [
    { key: 'SDE_CONTAINS', value: true },
    { key: 'SDE_MATCHES1', value: false },
  ];

  countryCodeMap: Record<string, string> = {
    AR: 'ARGENTINA',
    AU: 'AUSTRALIA',
    BE: 'BELGIUM',
    BR: 'BRAZIL',
    CA: 'CANADA',
    CL: 'CHILE',
    CN: 'CHINA',
    CO: 'COLOMBIA',
    DK: 'DENMARK',
    FI: 'FINLAND',
    FR: 'FRANCE',
    DE: 'GERMANY',
    HK: 'HONG_KONG',
    IN: 'INDIA',
    ID: 'INDONESIA',
    IE: 'IRELAND',
    IL: 'ISRAEL',
    IT: 'ITALY',
    JP: 'JAPAN',
    KR: 'KOREA',
    MX: 'MEXICO',
    NL: 'NETHERLANDS',
    NO: 'NORWAY',
    PY: 'PARAGUAY',
    PE: 'PERU',
    PL: 'POLAND',
    PT: 'PORTUGAL',
    SG: 'SINGAPORE',
    ES: 'SPAIN',
    SE: 'SWEDEN',
    TW: 'TAIWAN',
    TH: 'THAILAND',
    TR: 'TURKEY',
    GB: 'UK',
    UY: 'URUGUAY',
    US: 'US',
    VE: 'VENEZUELA',
  };

  markupTypes = {
    name: 'markupType',
    label: 'SDE_MARKUP_TYPE',
    icon: 'markup-type',
    selectedValue: {
      key: 'SDE_REDACT',
      value: 'redact',
      icon: 'markup-type-redact',
    },
    type: SettingType.Select,
    options: [
      {
        key: 'SDE_REDACT',
        value: 'redact',
        icon: 'markup-type-redact',
        isDefault: true,
      },
      {
        key: 'SDE_HIGHLIGHT',
        value: 'highlight',
        icon: 'markup-type-highlight',
      },
      {
        key: 'SDE_RTE_UNDERLINE',
        value: 'underline',
        icon: 'markup-type-underline',
      },
    ],
  };

  constructor(
    private http: HttpClient,
    private modalService: NgbModal,
    private userService: UserService,
  ) {
    super();
    const userCountry = this.userService?.user?.country_code ?? '';
    this.countryCode = this.countryCodeMap[userCountry] ?? 'US';
  }

  ngOnInit() {
    this.validation.emit(false);
    this.loadSavedConfiguration();
    this.getCountries();
    this.cwForm?.valueChanges?.subscribe(() => {
      const invalid = this.cwForm.status === 'INVALID' ? true : false;
      if (this.cwFormIsInvalid !== invalid) {
        this.cwFormIsInvalid = invalid;
        this.validate();
      }
    });

    this.standardFieldsLabel = this.isMfd ? '' : 'SDE_STANDARD_REDACTION_ITEMS';
  }

  validate() {
    this.isInvalid =
      this.cwFormIsInvalid ||
      this.defaultFieldIsInvalid ||
      this.customFieldIsInvalid ||
      !this.standardFields?.length;
    this.validation.emit(!this.isInvalid);
  }

  getCountries() {
    this.http
      .get<CountryDTO[]>(`${this.baseUrl}countries/${this.langCode}`)
      .subscribe(
        (resp) => {
          resp.forEach((e) => {
            this.countries.push({ key: e.id, value: e.country });
          });

          this.countryCode = this.value2 ? this.value2 : this.countryCode;
          const country = this.countries.find(
            (x) => x.key === this.countryCode,
          );
          if (country) {
            this.selectedCountry = country;
          }
          this.getStandardFieldsByCountry(this.countryCode);
        },
        (err) => {
          console.error(err);
        },
      );
  }

  getStandardFieldsByCountry(country: string) {
    this.isLoading = true;
    this.http
      .get<StandardField[]>(`${this.baseUrl}types/${country}/${this.langCode}`)
      .subscribe(
        (resp) => {
          this.standardFields = resp;
          this.standardFields.sort((a, b) => this.compareByDisplayText(a, b));
          this.defaultFields = this.standardFields.filter(
            (x) => x.preferred === true,
          );
          this.isLoading = false;

          // load saved configuration
          if (this.isSavedConfig && this.value) {
            this.defaultFields.forEach((x) => (x.toggled = false));
            const standardFields = this.redactConfig.standardFields;
            standardFields.forEach((x) => {
              let field = this.defaultFields.find(
                (d) => d.googleId === x.googleId,
              );
              if (!field) {
                field = this.standardFields.find(
                  (d) => d.googleId === x.googleId,
                );
                // If we found it in standardFields then add it (else it must be a removed field)
                if (field) {
                  this.defaultFields.push(field);
                }
              }
              if (field) {
                field.preferred = true;
                field.toggled = true;
              }
            });
            this.defaultFields.sort((a, b) => this.compareByDisplayText(a, b));
            this.isSavedConfig = false;
          } else {
            this.defaultFields.forEach((x) => (x.toggled = true));
            this.updateRedactConfig();
          }
          this.defaultFieldIsInvalid =
            this.defaultFields.filter((x) => x.toggled === true).length === 0 &&
            this.customFields.length === 0;
          this.validate();
        },
        () => {
          // empty
        },
      );
  }

  loadSavedConfiguration() {
    if (this.value) {
      this.redactConfig = JSON.parse(this.value);
      this.isSavedConfig = true;
    }

    if (!this.redactConfig.markupType) {
      this.redactConfig.markupType = 'redact';
    }
    const selectedMarkup = this.markupTypes.options.find(
      (x) => x.value == this.redactConfig.markupType,
    );
    if (selectedMarkup) {
      this.markupTypes.selectedValue = selectedMarkup;
    }

    if (this.redactConfig.customFields.length) {
      this.customFields = this.redactConfig.customFields;
      this.customFields.forEach((x) => {
        if (x.contains) {
          x.selectedValue = { key: 'SDE_CONTAINS', value: x.contains };
        } else {
          x.selectedValue = { key: 'SDE_MATCH', value: x.contains };
        }

        if (!x.id && x.id !== 0) {
          // if the saved workflow was saved before id was added to the CustomField object ids needs to be added to the loaded CustomField and the customFieldId needs to be incremented.
          x.id = this.customFieldId;
          this.customFieldId++;
        } else {
          // else if CustomField does have ids, make sure we increment customFieldId or we could end up with custom fields with the same id/name which will break validation
          this.customFieldId = x.id + 1;
        }
      });
    }
  }

  editPreferredList() {
    const windowStype = this.isMfd ? 'xrx-popup' : 'xrx-modal';
    const modalRef = this.modalService.open(
      StandardRedactionFieldsModalComponent,
      { windowClass: windowStype },
    );
    modalRef.componentInstance.list = this.standardFields;
    modalRef.componentInstance.isMfd = this.isMfd;

    modalRef.result
      .then(() => {
        this.defaultFields = this.standardFields.filter(
          (x) => x.preferred === true,
        );
        this.defaultFields.forEach(
          (x) =>
            (x.toggled =
              x.toggled === undefined || x.toggled == null ? true : x.toggled),
        );
        this.defaultFields.sort((a, b) => this.compareByDisplayText(a, b));
        this.onDefaultFieldChange();
      })
      .catch(() => {
        // dismissed.
        this.standardFields.forEach((x) => {
          if (this.defaultFields.some((f) => f.googleId === x.googleId)) {
            x.preferred = true;
          } else {
            x.preferred = false;
          }
        });
      });
  }

  onDefaultFieldChange() {
    this.defaultFieldIsInvalid =
      this.defaultFields.filter((x) => x.toggled === true).length === 0 &&
      this.customFields.length === 0;
    this.updateRedactConfig();
    this.validate();
  }

  removeCustomWord(index: number) {
    this.customFields.splice(index, 1);
    this.onDefaultFieldChange();
    this.updateRedactConfig();
  }

  addCustomWord() {
    this.customFields.push({
      id: this.customFieldId,
      text: '',
      contains: true,
      selectedValue: { key: 'SDE_CONTAINS', value: true },
    });
    this.onDefaultFieldChange();
    this.addCustomWordDisabled = true;
    this.customFieldId++;
  }

  openAddCustomWordModal() {
    const modalRef = this.modalService.open(AddCustomStringModalComponent, {
      windowClass: 'xrx-popup',
    });
    modalRef.result
      .then((x) => {
        let customField: CustomField;
        if (x.value) {
          customField = {
            id: this.customFieldId,
            text: x.customString,
            contains: x.value,
            selectedValue: { key: 'SDE_CONTAINS', value: x.value },
          };
        } else {
          customField = {
            id: this.customFieldId,
            text: x.customString,
            contains: x.value,
            selectedValue: { key: 'SDE_MATCH', value: x.value },
          };
        }
        this.customFields.push(customField);
        this.onDefaultFieldChange();
        this.customFieldId++;
      })
      .catch(() => {
        // dismissed.
      });
  }

  openSelectCountryModal() {
    const modalRef = this.modalService.open(SelectCountryModalComponent, {
      windowClass: 'xrx-popup',
    });
    modalRef.componentInstance.countryList = this.countries;
    modalRef.componentInstance.selectedValue = this.selectedCountry;

    modalRef.result
      .then((x) => {
        if (x.key) {
          this.selectedCountry = x;
          this.onCountrySelected();
        }
      })
      .catch(() => {
        // dismissed.
      });
  }

  onCountrySelected() {
    this.getStandardFieldsByCountry(this.selectedCountry.key);
    this.updateRedactConfig();
    this.value2 = this.selectedCountry.key;
    this.change2.emit(this.value2);
  }

  onMarkupSelected() {
    this.updateRedactConfig();
  }

  onCustomWordChange(e: Event) {
    e.stopPropagation();
    this.updateRedactConfig();
  }

  onMatchCriteriaChange() {
    this.updateRedactConfig();
  }

  updateRedactConfig() {
    this.redactConfig.markupType = this.markupTypes.selectedValue.value;

    if (!this.isLoading) {
      this.redactConfig.standardFields = [];
      this.defaultFields.forEach((x) => {
        if (x.toggled) {
          this.redactConfig.standardFields.push({
            id: x.id,
            googleId: x.googleId,
          });
        }
      });
    }

    this.redactConfig.customFields = [];
    this.customFields.forEach((x) => {
      if (x.text.length) {
        this.redactConfig.customFields.push({
          id: x.id,
          text: x.text,
          contains: (x?.selectedValue?.value as boolean) || false,
          selectedValue: null,
        });
      }
    });
    this.customFieldIsInvalid =
      this.redactConfig.standardFields.length === 0 &&
      this.redactConfig.customFields.length === 0 &&
      this.customFields.length > 0;

    this.value = JSON.stringify(this.redactConfig);
    this.change.emit(this.value);
  }

  // Private helper comparator to safely compare optional displayText values.
  private compareByDisplayText(a: StandardField, b: StandardField): number {
    const ta = a.displayText ?? '';
    const tb = b.displayText ?? '';
    return ta.localeCompare(tb);
  }
}

interface CountryDTO {
  id: string;
  country: string;
}

interface CountryOption {
  key: string;
  value: string;
}
export interface StandardField {
  id: number;
  googleId: string;
  displayText?: string;
  descriptionText?: string;
  preferred?: boolean;
  toggled?: boolean;
}

interface CustomField {
  id: number;
  text: string;
  contains: boolean;
  selectedValue: OptionListOptions | null;
}

interface RedactConfig {
  markupType: string;
  standardFields: StandardField[];
  customFields: CustomField[];
}
